import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '../../_alert';
import { NgxSpinnerService } from 'ngx-spinner';
import { DepartmentService } from '../../services/jrni/department.service';
import { DatastoreService } from '../../services/datastore.service';
import { ServicesService } from '../../services/jrni/services.service';
import { TranslateService } from '@ngx-translate/core';
import { BasketService } from '../../services/jrni/basket.service';
import { ApiService } from '../../services/jrni/api.service';
import { AvailabilityService } from '../../services/jrni/availability.service';
import { AppConfig } from '../../app.config';
import { CivicaPaylinkService } from '../../services/civica-paylink.service';
import { QuestionsService } from '../../services/questions.service';
import { Subscription, timer } from 'rxjs';

/**
 * Civica Paylink payment return leg — PRE-BASKET (book-after-payment).
 *
 * The payer lands on `payment-verify/:company/:basket/:serviceItems` from
 * Civica's receipt page (ReceiptPageFinishURL), or via bl-civica-api's
 * notify receiver bouncing the browser on with `basket`, `service_item`
 * and `response_code` appended as query params — extra query params on
 * top of the path params are expected and ignored here.
 *
 * No booking exists yet — bl-civica-api stored the intent and, once Civica
 * confirms payment, the n8n Civica Post-Basket Verify workflow checks out
 * the JRNI basket and links the extra items. This component polls
 * /payment/civica/pre-basket/find until the row shows the JRNI purchase
 * (data.purchase_id / status PAID), then hydrates and navigates to
 * confirmation. A FAILED_* or CANCELLED status shows the failure panel.
 *
 * With Civica's "Automatic Return Response" ON the payment is confirmed
 * server-side even if the payer never clicks Finish — so this page cannot
 * assume the session arrives quickly (or at all) after payment, and
 * restores its state from the pre-basket row (extractInitialData) rather
 * than from anything held in memory.
 *
 * A pre-payment back-out lands here with ?cancelled=true (the Paylink
 * BackButtonURL) — the basket is cleared and the journey restarted.
 */
@Component({
  selector: 'app-verify-payment',
  templateUrl: './verify-payment.component.html',
  styleUrls: ['./verify-payment.component.scss'],
})
export class VerifyPaymentComponent implements OnInit, OnDestroy {
  pageTitle: string;
  verifyPageText: string;
  webhookResponseStatus = '';
  maxRetriesReached = false;
  paymentFailed = false;
  paymentAlreadyPaid = false;
  failed = false;
  chargeId = '';
  receipt = '';
  ref = '';

  companyId: any = null;
  basket: string = null;
  serviceItems: string[] = [];
  private serviceItem: string = null;

  private selectedSlot: any;
  private selectedLocation: any;
  private parent: any;
  private config: any;

  private pollSub: Subscription;
  private retries = 300;
  private initialDataExtracted = false;
  private findErrorCount = 0;
  private maxFindErrors = 10;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private departmentService: DepartmentService,
    private alertService: AlertService,
    private spinner: NgxSpinnerService,
    private datastoreService: DatastoreService,
    private civicaPaylinkService: CivicaPaylinkService,
    private translateService: TranslateService,
    private servicesService: ServicesService,
    private basketService: BasketService,
    private apiService: ApiService,
    private availabilityService: AvailabilityService,
    private questionsService: QuestionsService
  ) {}

  async ngOnInit() {
    this.spinner.show();

    const appConfig: any = await this.apiService.getConfig(
      AppConfig.settings.core.company_id
    );
    this.config = appConfig;
    this.datastoreService.config = appConfig;
    if (
      this.config?.journey_config?.ceremonies?.payment_verify_retries !==
      undefined
    ) {
      this.retries = parseInt(
        this.config.journey_config.ceremonies.payment_verify_retries
      );
      console.log(`Retries set to ${this.retries}`);
    }

    this.route.params.subscribe(async (params) => {
      console.log('url params', params);
      if (params.company) {
        this.companyId = params.company;
      }
      if (params.basket) {
        this.basket = params.basket;
      }
      if (params.serviceItems) {
        this.serviceItems = params.serviceItems.split(',').filter((s) => s);
        if (this.serviceItems.length > 0) {
          this.serviceItem = this.serviceItems[0];
        }
      }

      if (!this.basket || !this.serviceItem) {
        console.error('Missing basket/service item on the verify route');
        this.spinner.hide();
        this.pageTitle = 'Payment verification error';
        this.verifyPageText = 'Missing payment details. Please try again.';
        this.paymentFailed = true;
        return;
      }

      // NRPO-38 pattern: the payer backed out on the gateway. Paylink's
      // BackButtonURL is this page tagged with ?cancelled=true (a normal
      // return never carries the marker), so the user would otherwise get
      // stuck on the "Verifying payment" spinner. Clear the basket they
      // abandoned and restart the journey.
      if (this.route.snapshot.queryParams['cancelled']) {
        await this.restartJourneyAfterCancel();
        return;
      }

      this.pageTitle = 'Verifying payment';
      this.verifyPageText = 'Please wait while we confirm your payment...';
      await this.initPreBasket();
    });
  }

  ngOnDestroy() {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
  }

  // ---- Civica Paylink pre-basket (book-after-payment) ----

  private async initPreBasket() {
    console.log(
      'Verify payment (pre-basket) — company:',
      this.companyId,
      'basket:',
      this.basket,
      'serviceItem:',
      this.serviceItem
    );

    // Resolve the location so the confirmation page can find it; prefer the
    // child company the booking sits under.
    try {
      const parent = await this.departmentService.getParentDepartment();
      this.parent = parent;
      const children = await this.departmentService.getChildDepartments(
        parent
      );
      for (const location of children) {
        if (location.id == this.companyId) {
          await this.departmentService.setLocation(location);
          this.selectedLocation = location;
          break;
        }
      }
    } catch (err) {
      console.error('Error resolving location:', err);
    }

    this.startFindPolling();
  }

  private startFindPolling() {
    // Poll immediately, then every 2 seconds until the backend worker has
    // created the JRNI booking (or reports a failure).
    const source = timer(0, 2000);
    this.pollSub = source.subscribe(async (attempt) => {
      if (attempt >= this.retries) {
        this.pollSub.unsubscribe();
        console.error(`Retry limit of ${this.retries} reached`);
        this.spinner.hide();
        this.webhookResponseStatus = '';
        this.maxRetriesReached = true;
        this.pageTitle = 'Payment verification timed out';
        this.verifyPageText = '';
        this.alertService.error(
          this.translateService.instant('COMMON.PAYMENT_MAX_RETRIES_REACHED', {
            serviceId: this.basket,
          })
        );
        return;
      }

      try {
        const found: any = await this.civicaPaylinkService.preBasketFind(
          this.companyId,
          this.basket,
          this.serviceItem
        );
        console.log(`preBasketFind (attempt ${attempt + 1}):`, found);

        if (found && found.data) {
          if (!this.initialDataExtracted) {
            await this.extractInitialData(found);
            this.initialDataExtracted = true;
          }

          const status = `${found.status || ''}`;
          if (status) {
            this.webhookResponseStatus = `COMMON.${status}`;
          }

          // Checkout / processing failures.
          if (
            status.indexOf('FAILED') !== -1 ||
            status.indexOf('ERR_') !== -1 ||
            status === 'CANCELLED'
          ) {
            this.pollSub.unsubscribe();
            this.handlePreBasketFailure(found);
            return;
          }

          // purchase_id is written once the worker has checked out the basket
          // and created the JRNI booking (BOOKING_MADE / PAID).
          if (found.data.purchase_id || status === 'PAID') {
            this.pollSub.unsubscribe();
            await this.hydrateAndNavigate(found);
            return;
          }

          // Payment confirmed by Civica but booking not yet created.
          if (found.data.payment_notification) {
            this.verifyPageText =
              'Payment received. Finalising your booking, please wait...';
          }
        }
      } catch (err) {
        console.error('Error in preBasketFind polling:', err);
        this.findErrorCount++;
        if (this.findErrorCount >= this.maxFindErrors) {
          this.pollSub.unsubscribe();
          this.spinner.hide();
          this.webhookResponseStatus = '';
          this.pageTitle = 'Payment verification error';
          this.verifyPageText = this.translateService.instant('PAYMENT.ERROR');
          this.paymentFailed = true;
        }
      }
    });
  }

  // Restore the journey state carried on the pre-basket row — the payer may
  // arrive in a fresh tab long after Civica's receipt page.
  private async extractInitialData(found: any) {
    const data = found.data;
    if (!data) return;

    if (data.extra_items?.lookupResponses) {
      this.datastoreService.lookupResponses = data.extra_items.lookupResponses;
    }
    if (data.extra_items?.selectedAddress) {
      this.datastoreService.selectedAddress = data.extra_items.selectedAddress;
    }

    if (data.auth_token) {
      localStorage.setItem('auth_token', data.auth_token);
    }

    if (data.journey_route) {
      this.datastoreService.journeyRoute = data.journey_route;
    }

    if (data.detail_group) {
      this.getCompanyQuestions(data.detail_group);
    }

    if (data.slot) {
      this.selectedSlot = data.slot;
      this.datastoreService.selectedSlot = data.slot;
      await this.availabilityService.setSelectedSlot(data.slot);
    }
    this.datastoreService.extraDuration = data.extra_duration || 0;

    if (data.extra_items?.fees) {
      this.datastoreService.serviceFees = data.extra_items.fees;
    }
  }

  private async hydrateAndNavigate(found: any) {
    try {
      const data = found.data;
      const location =
        this.selectedLocation || (await this.departmentService.getLocation());

      const purchase: any = await this.apiService.findPurchase(
        location,
        data.purchase_id
      );
      const booking = purchase._embedded.bookings[0];
      this.datastoreService.booking = booking;
      this.datastoreService.purchase = purchase;

      const service: any = await this.apiService.getService(
        location,
        booking.service_id
      );
      this.datastoreService.selectedService = service;
      await this.servicesService.set(service);

      localStorage.setItem('booking', JSON.stringify(booking));
      localStorage.setItem('date', JSON.stringify(this.selectedSlot));
      localStorage.setItem('location', JSON.stringify(location));
      localStorage.setItem('service', JSON.stringify(service));

      this.spinner.hide();
      this.router.navigate(['confirmation']);
    } catch (err) {
      console.error('Error hydrating confirmation data:', err);
      this.spinner.hide();
      this.webhookResponseStatus = '';
      this.pageTitle = 'Payment verification error';
      this.verifyPageText = this.translateService.instant('PAYMENT.ERROR');
      this.paymentFailed = true;
    }
  }

  private handlePreBasketFailure(found: any) {
    console.error(
      `Pre-basket row reported ${found?.status} for ${this.basket}`
    );
    this.chargeId =
      found?.data?.auth_code ||
      found?.data?.payment_notification?.PaymentAuthorisationCode ||
      '';
    this.receipt =
      found?.data?.receipt ||
      found?.data?.payment_notification?.IncomeManagementReceiptNumber ||
      '';
    this.ref = found?.ref || '';
    this.spinner.hide();
    this.webhookResponseStatus = '';
    this.failed = true;
  }

  // NRPO-38: the user abandoned the payment (gateway Back/Cancel).
  // Clear the basket they abandoned and restart the journey on the correct
  // landing page.
  async restartJourneyAfterCancel() {
    const authToken: string = localStorage.getItem('auth_token');
    try {
      await this.apiService.deleteBaskets({ id: this.companyId });
    } catch (err) {
      console.warn('Could not clear basket on payment cancel/back:', err);
    }
    this.basketService.currentBasket = null;
    if (localStorage.getItem('agent') && authToken) {
      // Agent/telephony journey: return to the admin services page.
      window.location.assign(`/services?admin-only=true&auth_token=${authToken}`);
    } else {
      // Public journey: return to the landing page for the journey they were on.
      const journey = sessionStorage.getItem('journey');
      window.location.assign(journey ? `/start/${journey}` : `/start`);
    }
  }

  getCompanyQuestions(detailGroup) {
    this.questionsService
      .getCompanyQuestions(detailGroup)
      .subscribe((res: any) => {
        this.datastoreService.companyQuestions = res.questions;
      });
  }
}
